import React, { useState, useCallback } from 'react';
import _isNil from 'lodash/isNil';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import { ExternalLink } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import Explorer from './Explorer';
import { Button } from '@lifeomic/chroma-react/components/Button';
import JMESParser from '../../../../util/JMESParser';

const messages = defineMessages({
  noValue: {
    id: 'patientViewer.jmesField.noValue',
    defaultMessage: 'Not Available',
  },
  dialogTitle: {
    id: 'patientViewer.jmesField.dialogTitle',
    defaultMessage: 'Data Explorer',
  },
  openEditor: {
    id: 'patientViewer.jmesField.openEditor',
    defaultMessage: 'Open Editor',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  openEditor: {
    marginTop: theme.spacing(-0.75),
    padding: theme.spacing(0, 0, 0, 0.875),
  },
}));

export type JMESDisplayClasses = GetClasses<typeof useStyles>;

export interface JMESDisplayOwnProps {
  jmesParser: JMESParser;
  displayNotAvailable: boolean;
  isInEditMode: boolean;
  color: 'default' | 'inverse' | 'positive' | 'negative';
}

export type JMESDisplayProps = JMESDisplayOwnProps;

const JMESDisplay = (props: JMESDisplayProps) => {
  const classes = useStyles(props);
  const formatMessage = useIntl().formatMessage;
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const { displayNotAvailable, jmesParser, isInEditMode } = props;

  const openDialog = useCallback(() => {
    setIsEditorOpen(true);
  }, [setIsEditorOpen]);

  const closeDialog = useCallback(() => {
    setIsEditorOpen(false);
  }, [setIsEditorOpen]);

  const fallbackValue = displayNotAvailable
    ? formatMessage(messages.noValue)
    : null;
  let displayValue = fallbackValue;

  if (jmesParser) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    displayValue = jmesParser.parseToRenderableValue(fallbackValue);
  }

  const renderValue = () => {
    const conditionalIconButton = !isInEditMode ? null : (
      <IconButton
        className={classes.openEditor}
        color={props.color}
        aria-label={formatMessage(messages.openEditor)}
        size={0}
        icon={ExternalLink}
        onClick={openDialog}
        aria-haspopup="dialog"
        aria-expanded={isEditorOpen}
      />
    );

    return (
      <>
        {!_isNil(displayValue) ? displayValue : null}
        {conditionalIconButton}
      </>
    );
  };

  const renderDialog = () => (
    <Modal
      isFullScreen
      onDismiss={closeDialog}
      isOpen={isEditorOpen}
      title={formatMessage(messages.dialogTitle)}
      actions={
        <Button variant="contained" onClick={closeDialog}>
          <FormattedMessage id="jmesExplorer.cancel" defaultMessage="Cancel" />
        </Button>
      }
    >
      <Explorer
        isEditorOpen={isEditorOpen}
        jmesParser={jmesParser}
        onClose={closeDialog}
      />
    </Modal>
  );

  return (
    <>
      {renderValue()}
      {renderDialog()}
    </>
  );
};

export default React.memo(JMESDisplay);
